import { useAtom } from 'jotai';

import { useGetApiV1Person } from '@/shared/api/generated';

import { personFiltersAtom } from '../store/atoms/personFiltersAtom';
import type { PersonFilters } from '../types/person';

const PAGE_SIZE = 20;

/**
 * Hook para listar pessoas com filtros e paginação server-side.
 *
 * @returns Dados paginados de pessoas com estado de loading/error
 *
 * @example
 * ```tsx
 * const { data, isLoading, totalCount, currentPage, totalPages } = usePersonList();
 * ```
 */
export function usePersonList() {
  const [filters] = useAtom(personFiltersAtom);

  const active = filters.status === 'all' ? undefined : filters.status === 'active';

  const query = useGetApiV1Person({
    page: filters.page,
    pageSize: PAGE_SIZE,
    ...(filters.name ? { people: filters.name } : {}),
    ...(filters.email ? { email: filters.email } : {}),
    ...(filters.cpf ? { cpf: filters.cpf } : {}),
    ...(filters.companyId !== null ? { companyId: filters.companyId } : {}),
    ...(active !== undefined ? { active } : {}),
  });

  return {
    data: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    totalCount: query.data?.totalCount ?? 0,
    currentPage: query.data?.currentPage ?? filters.page,
    totalPages: query.data?.totalPages ?? 1,
  } as const;
}

/**
 * Hook para gerenciar filtros da listagem de pessoas.
 *
 * @returns Filtros atuais e funções para atualizar e resetar
 */
export function usePersonFilters() {
  const [filters, setFilters] = useAtom(personFiltersAtom);

  const updateFilters = (partial: Partial<PersonFilters>) => {
    setFilters(prev => {
      const resetPage = 'page' in partial ? {} : { page: 1 };
      return { ...prev, ...resetPage, ...partial };
    });
  };

  const resetFilters = () => {
    setFilters({ name: '', email: '', cpf: '', companyId: null, status: 'all', page: 1 });
  };

  return { filters, updateFilters, resetFilters } as const;
}
